import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../ui/popover';
import { Calendar } from '../ui/calendar';
import { AlertTriangle, Calendar as CalendarIcon, RefreshCw, Search, ShieldCheck, Upload, UserCog, X } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { formatDT, getFileTimestamp } from '../../utils/timezone';
import { csvCell } from '../../utils/csv';
import { useAuth } from '../../contexts/AuthContext';
import { PaginationFooter, usePersistedPageSize } from '../shared/PaginationFooter';
import { DateRangePresetFilter } from '../shared/DateRangePresetFilter';
import { PendingReviewsPanel } from './PendingReviewsPanel';

const CORRECTION_TYPE_OPTIONS = [
  { value: 'all', label: 'All correction types' },
  { value: 'active_driver', label: 'Active Driver Corrections' },
  { value: 'directory_assignment', label: 'Assigned from People List' },
  { value: 'override_declined', label: 'Overrode Declined Response' },
  { value: 'no_driver', label: 'Confirmed No Driver' },
  { value: 'auto_assignment', label: 'Auto-Assignment Corrections' },
];

const ACTOR_KIND_LABELS = { owner: 'Owner', sub_account: 'Sub Account', admin: 'Admin' };
const ACTOR_KIND_CLASSES = {
  owner: 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/40 dark:text-blue-100 dark:border-blue-700',
  sub_account: 'bg-teal-100 text-teal-800 border-teal-300 dark:bg-teal-900/40 dark:text-teal-100 dark:border-teal-700',
  admin: 'bg-slate-200 text-slate-800 border-slate-300 dark:bg-slate-700 dark:text-slate-100 dark:border-slate-600',
};

const TYPE_BADGE_CLASSES = {
  active_driver: 'bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-900/40 dark:text-amber-100 dark:border-amber-700',
  directory_assignment: 'bg-sky-100 text-sky-900 border-sky-300 dark:bg-sky-900/40 dark:text-sky-100 dark:border-sky-700',
  override_declined: 'bg-purple-100 text-purple-900 border-purple-300 dark:bg-purple-900/40 dark:text-purple-100 dark:border-purple-700',
  no_driver: 'bg-slate-200 text-slate-900 border-slate-300 dark:bg-slate-700 dark:text-slate-100 dark:border-slate-600',
  auto_assignment: 'bg-emerald-100 text-emerald-900 border-emerald-300 dark:bg-emerald-900/40 dark:text-emerald-100 dark:border-emerald-700',
  other: 'bg-slate-100 text-slate-800 border-slate-300 dark:bg-slate-800 dark:text-slate-100 dark:border-slate-700',
};

export const DriverCorrectionsTab = ({ onPendingCountChange }) => {
  const { api } = useAuth();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = usePersistedPageSize('driver-corrections', 10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [correctionType, setCorrectionType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [dateRange, setDateRange] = useState({ from: undefined, to: undefined });
  const [dateResetKey, setDateResetKey] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAllLoading, setSelectAllLoading] = useState(false);


  useEffect(() => {
    const handle = setTimeout(() => setSearchDebounced(searchQuery.trim()), 300);
    return () => clearTimeout(handle);
  }, [searchQuery]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('skip', String(page * pageSize));
      params.set('limit', String(pageSize));
      if (correctionType && correctionType !== 'all') {
        params.set('correction_type', correctionType);
      }
      if (searchDebounced) {
        params.set('search', searchDebounced);
      }
      if (dateRange.from) {
        params.set('from_date', dateRange.from.toISOString());
      }
      if (dateRange.to) {
                params.set('to_date', dateRange.to.toISOString());
      }
      const res = await api.get(`/people/driver-corrections?${params.toString()}`);
      setRows(res.data?.logs || []);
      setTotal(res.data?.total || 0);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Failed to load driver corrections';
      setError(detail);
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [api, page, pageSize, correctionType, searchDebounced, dateRange.from, dateRange.to]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  // Reset to page 1 whenever a filter changes so we always land on real results.
  useEffect(() => {
    setPage(0);
  }, [searchDebounced, correctionType, dateRange.from, dateRange.to]);

  const clearFilters = () => {
    setSearchQuery('');
    setCorrectionType('all');
    setDateRange({ from: undefined, to: undefined });
    setPage(0);
  };

  // CSV export — exports selected rows. When the user has ticked rows across
  // multiple pages via "Select All", we fetch the full matching dataset from
  // the server so the CSV contains every selected row (not just those on the
  // current page). Matches the People-page "disabled until items are ticked" UX.
  const exportCsv = useCallback(async () => {
    if (!selectedIds.length) {
      toast.info('No rows to export');
      return;
    }
    // Rows we already have loaded on the current page.
    let rowsToExport = rows.filter((r) => selectedIds.includes(r.id));
    // If some selected IDs live on other pages, fetch the full filtered set
    // once and pick out the remainder. Keeps a single round-trip regardless of
    // how many pages the selection spans.
    if (rowsToExport.length < selectedIds.length) {
      try {
        const params = new URLSearchParams();
        params.set('skip', '0');
        params.set('limit', String(Math.max(total, selectedIds.length)));
        if (correctionType && correctionType !== 'all') params.set('correction_type', correctionType);
        if (searchDebounced) params.set('search', searchDebounced);
        if (dateRange.from) params.set('from_date', dateRange.from.toISOString());
        if (dateRange.to) {
                    params.set('to_date', dateRange.to.toISOString());
        }
        const res = await api.get(`/people/driver-corrections?${params.toString()}`);
        const all = res.data?.logs || [];
        rowsToExport = all.filter((r) => selectedIds.includes(r.id));
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Failed to fetch selected rows for export');
        return;
      }
    }
    if (!rowsToExport.length) {
      toast.info('No rows to export');
      return;
    }
    const headers = ['When', 'Type', 'Details', 'Reason', 'Confirmed By', 'Account Type', 'IP', 'From Driver', 'To Driver', 'From Person ID', 'To Person ID'];
    const csvRows = rowsToExport.map((r) => [
      r.timestamp ? formatDT(r.timestamp, { seconds: true }) : '',
      r.correction_label || r.correction_type || '',
      r.details || '',
      r?.changes?.reason || '',
      r.actor_name ? `${r.actor_name} (${r.actor_email || ''})` : (r.actor_email || ''),
      ACTOR_KIND_LABELS[r.actor_kind] || '',
      r.ip_address || '',
      r.previous_driver_name || '',
      r.driver_name || '',
      r?.changes?.previous_driver_person_id || '',
      r?.changes?.driver_person_id || '',
    ]);
    const csv = [headers, ...csvRows].map((row) => row.map(csvCell).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `driver-corrections-${getFileTimestamp()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rowsToExport.length} row${rowsToExport.length === 1 ? '' : 's'}`);
  }, [api, rows, selectedIds, total, correctionType, searchDebounced, dateRange.from, dateRange.to]);

  return (
    <div className="space-y-4" data-testid="driver-corrections-tab-content">
      {/* Pending ballots queue — complete outstanding driver reviews from here. */}
      <PendingReviewsPanel api={api} onCountChange={onPendingCountChange} onResolved={fetchRows} />

      {/* Header — matches the Shared Evidence pattern: icon + title + description
          on top; filters (search / type / date / export / refresh) on the next
          row; count badges on their own row underneath. */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <UserCog className="w-5 h-5 text-foreground" />
            <h2 className="text-xl font-semibold" data-testid="driver-corrections-title">Driver Corrections</h2>
          </div>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">Manager overrides, directory assignments, and No-Driver confirmations</p>
        </div>
      </div>

      {/* Filters row */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search actor, details..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-8 pl-8"
            data-testid="driver-corrections-search"
          />
          {searchQuery && (
            <Button type="button" variant="ghost" size="sm" className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 p-0" onClick={() => setSearchQuery('')} data-testid="driver-corrections-clear-search">
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
        <Select value={correctionType} onValueChange={setCorrectionType}>
          <SelectTrigger className="h-8 w-56" data-testid="driver-corrections-type-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CORRECTION_TYPE_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value} data-testid={`driver-corrections-type-${opt.value}`}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {/* Date range — Event Log "All Time" preset filter (shared component). */}
        <DateRangePresetFilter value={dateRange} onChange={(r) => { setDateRange(r); setPage(0); }} testIdPrefix="driver-corrections-date" resetSignal={dateResetKey} />
        <Button variant="outline" size="sm" onClick={exportCsv} disabled={selectedIds.length === 0} data-testid="driver-corrections-export">
          <Upload className="w-4 h-4 mr-2" />
          {selectedIds.length > 0 ? `Export (${selectedIds.length})` : 'Export'}
        </Button>
        <Button variant="outline" size="sm" onClick={() => { setDateRange({ from: undefined, to: undefined }); setDateResetKey(k => k + 1); setPage(0); fetchRows(); }} disabled={loading} data-testid="driver-corrections-refresh">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
        {(searchQuery || correctionType !== 'all' || dateRange.from) && (
          <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="driver-corrections-clear-filters">
            <X className="w-4 h-4 mr-1" />
            Clear
          </Button>
        )}
      </div>

      {/* Count badges row — always visible (even when 0 rows) so the layout
          doesn't jump when filters clear the list. Matches Shared Evidence. */}
      <div className="flex flex-wrap items-center gap-2" data-testid="driver-corrections-counts-row">
        <Badge variant="outline" data-testid="driver-corrections-total-badge">{total} Total</Badge>
      </div>

      {/* Selection bar — "Select All" grabs every matching row from the server
          (across all pages) so the user's next Export covers the full filtered
          dataset, not just what happens to be on the current page. */}
      <div className="flex items-center gap-3 text-sm text-muted-foreground px-1 h-6 -my-1.5 overflow-hidden" data-testid="driver-corrections-selection-bar">
          <span data-testid="driver-corrections-selection-count">{selectedIds.length} selected</span>
{" "}
                  {selectedIds.length > 0 && (<>
          <Button variant="ghost" size="sm" className="h-6 px-2 text-xs"
            data-testid="driver-corrections-select-all-toggle"
            disabled={selectAllLoading}
            onClick={async () => {
              if (selectedIds.length >= total) {
                setSelectedIds([]);
                return;
              }
              try {
                setSelectAllLoading(true);
                const params = new URLSearchParams();
                params.set('skip', '0');
                params.set('limit', String(Math.max(total, 1)));
                if (correctionType && correctionType !== 'all') params.set('correction_type', correctionType);
                if (searchDebounced) params.set('search', searchDebounced);
                if (dateRange.from) params.set('from_date', dateRange.from.toISOString());
                if (dateRange.to) {
                                    params.set('to_date', dateRange.to.toISOString());
                }
                const res = await api.get(`/people/driver-corrections?${params.toString()}`);
                const allIds = (res.data?.logs || []).map((r) => r.id).filter(Boolean);
                setSelectedIds(allIds);
              } catch (err) {
                toast.error(err?.response?.data?.detail || 'Failed to select all rows');
              } finally {
                setSelectAllLoading(false);
              }
            }}
          >
            {selectAllLoading ? 'Selecting…' : (selectedIds.length >= total ? 'Deselect All' : 'Select All')}
          </Button>
          <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setSelectedIds([])} data-testid="driver-corrections-selection-clear">Clear</Button>
        </>)}
                  </div>

      {error && (
        <Card className="border-red-300 dark:border-red-800">
          <CardContent className="py-4 flex items-center gap-2 text-red-800 dark:text-red-200">
            <AlertTriangle className="w-4 h-4" />
            <span data-testid="driver-corrections-error">{error}</span>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          <Table data-testid="driver-corrections-table">
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox
                    data-testid="driver-corrections-select-all"
                    checked={rows.length > 0 && rows.every((r) => r.id && selectedIds.includes(r.id))}
                    onCheckedChange={(checked) => {
                      const pageIds = rows.map((r) => r.id).filter(Boolean);
                      setSelectedIds((prev) => checked
                        ? Array.from(new Set([...prev, ...pageIds]))
                        : prev.filter((id) => !pageIds.includes(id)));
                    }}
                  />
                </TableHead>
                <TableHead className="w-40">When</TableHead>
                <TableHead className="w-44">Type</TableHead>
                <TableHead>Details</TableHead>
                <TableHead className="w-52">Confirmed By</TableHead>
                <TableHead className="w-72">Change</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground" data-testid="driver-corrections-loading">
                    Loading driver corrections…
                  </TableCell>
                </TableRow>
              )}
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10">
                    <div className="flex flex-col items-center gap-2 text-muted-foreground">
                      <ShieldCheck className="w-10 h-10 opacity-40" />
                      <p className="text-sm" data-testid="driver-corrections-empty">No driver corrections match the current filters.</p>
                    </div>
                  </TableCell>
                </TableRow>
              )}
              {rows.map((row, idx) => {
                const type = row.correction_type || 'other';
                const changes = row.changes || {};
                const previous = changes.previous_driver_person_id;
                const next = changes.driver_person_id;
                return (
                  <TableRow key={row.id || `${row.timestamp}-${idx}`} data-testid={`driver-corrections-row-${idx}`}>
                    <TableCell>
                      <Checkbox
                        data-testid={`driver-corrections-row-select-${idx}`}
                        checked={!!row.id && selectedIds.includes(row.id)}
                        disabled={!row.id}
                        onCheckedChange={(checked) => {
                          if (!row.id) return;
                          setSelectedIds((prev) => checked
                            ? Array.from(new Set([...prev, row.id]))
                            : prev.filter((id) => id !== row.id));
                        }}
                      />
                    </TableCell>
                    <TableCell className="text-xs" data-testid={`driver-corrections-when-${idx}`}>
                      {row.timestamp ? formatDT(row.timestamp, { seconds: true }) : '—'}
                    </TableCell>
                    <TableCell>
                      <Badge className={`border ${TYPE_BADGE_CLASSES[type] || TYPE_BADGE_CLASSES.other}`} data-testid={`driver-corrections-type-badge-${idx}`}>
                        {row.correction_label || type}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm" data-testid={`driver-corrections-details-${idx}`}>
                      <div className="whitespace-normal break-words">{row.details || '—'}</div>
                      {changes.reason && (
                        <div className="text-xs text-muted-foreground mt-1">
                          <span className="font-medium">Reason:</span> {changes.reason}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-xs" data-testid={`driver-corrections-actor-${idx}`}>
                      <div className="font-medium">{row.actor_name || row.actor_email || '—'}</div>
                      {row.actor_name && row.actor_email && (
                        <div className="text-muted-foreground">{row.actor_email}</div>
                      )}
                      {row.actor_kind && (
                        <Badge
                          variant="outline"
                          className={`mt-1 text-[10px] px-1.5 py-0 ${ACTOR_KIND_CLASSES[row.actor_kind] || ''}`}
                          data-testid={`driver-corrections-confirmed-by-${idx}`}
                        >
                          {ACTOR_KIND_LABELS[row.actor_kind] || row.actor_kind}
                        </Badge>
                      )}
                      {row.ip_address && (
                        <div className="text-muted-foreground mt-0.5">{row.ip_address}</div>
                      )}
                    </TableCell>
                    <TableCell className="text-xs" data-testid={`driver-corrections-change-${idx}`}>
                      {previous ? (
                        <div>
                          <span className="text-muted-foreground">From:</span>{' '}
                          {row.previous_driver_name
                            ? <span className="font-medium">{row.previous_driver_name}</span>
                            : <span className="font-mono">{previous.slice(0, 12)}…</span>}
                        </div>
                      ) : null}
                      {next ? (
                        <div>
                          <span className="text-muted-foreground">To:</span>{' '}
                          {row.driver_name
                            ? <span className="font-medium">{row.driver_name}</span>
                            : <span className="font-mono">{next.slice(0, 12)}…</span>}
                        </div>
                      ) : (changes.manager_confirmed_no_driver ? (
                        <div className="italic text-muted-foreground">No Driver</div>
                      ) : null)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <PaginationFooter
            page={page}
            setPage={setPage}
            totalItems={total}
            itemsPerPage={pageSize}
            setItemsPerPage={setPageSize}
            testIdPrefix="driver-corrections"
            zeroIndexed
            disabled={loading}
            alwaysShow
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default DriverCorrectionsTab;
